import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper
} from '@mui/material';
import {
  LocationCity as CityIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { fetchCities, createCity, deleteCity } from '../citiesApi';
import { useTranslation } from 'react-i18next';

const Cities = () => {
  const { t } = useTranslation();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [cityName, setCityName] = useState('');

  useEffect(() => {
    loadCities();
  }, []);

  const loadCities = async () => {
    setLoading(true);
    try {
      const data = await fetchCities();
      setCities(data || []);
    } catch (error) {
      console.error('Error loading cities:', error);
      Swal.fire(t('error'), t('failedToLoadCities'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!cityName) {
      Swal.fire(t('error'), t('cityNameRequired'), 'error');
      return;
    }

    try {
      await createCity(cityName);
      Swal.fire(t('success'), t('cityCreatedSuccess'), 'success');
      setOpen(false);
      setCityName('');
      loadCities();
    } catch (error) {
      Swal.fire(t('error'), t('failedToSaveCity'), 'error');
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: t('areYouSure'),
      text: t('revertWarning'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('yesDeleteIt')
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteCity(id);
          Swal.fire(t('deleted'), t('cityDeletedSuccess'), 'success');
          loadCities();
        } catch (error) {
          Swal.fire(t('error'), t('failedToDeleteCity'), 'error');
        }
      }
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ maxWidth: 600, mx: 'auto' }}>
        <CardHeader
          title={t('manageCities')}
          subheader={t('manageCitiesSubheader')}
          avatar={<CityIcon color="primary" fontSize="large" />}
          action={
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
              {t('addCity')}
            </Button>
          }
          sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
        />
        <CardContent>
          <List>
            {cities.map((city) => (
              <ListItem key={city.id || city.Id} divider>
                <ListItemText primary={city.name || city.Name} />
                <ListItemSecondaryAction>
                  <IconButton edge="end" color="error" onClick={() => handleDelete(city.id || city.Id)}>
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            {cities.length === 0 && !loading && (
              <Typography align="center" sx={{ py: 2 }}>{t('noCitiesFound')}</Typography>
            )}
          </List>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('addCity')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('cityName')}
            fullWidth
            value={cityName}
            onChange={(e) => setCityName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button onClick={handleSave} variant="contained">{t('add')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Cities;
